import { useEffect, useRef, useCallback } from "react";

const MALE_VOICE_KEYWORDS = [
  "male",
  "man",
  "david",
  "mark",
  "alex",
  "fred",
  "google uk english male",
  "google us english",
];

const useTextToSpeech = ({
  language,
  rate = 1,
  pitch = 0.9, // slightly deeper
  volume = 2,
  preferMaleVoice = true,
}) => {
  const utteranceRef = useRef(null);

  useEffect(() => {
    if (!("speechSynthesis" in window)) return;

    const synth = window.speechSynthesis;
    const utterance = new SpeechSynthesisUtterance();

    utterance.lang = language;
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.volume = volume;

    const loadVoices = () => {
      const voices = synth.getVoices();

      const matchedVoices = voices.filter(
        (voice) => voice.lang === language
      );

      if (matchedVoices.length > 0) {
        let selectedVoice = matchedVoices[0];

        if (preferMaleVoice) {
          const maleVoice = matchedVoices.find((voice) =>
            MALE_VOICE_KEYWORDS.some((keyword) =>
              voice.name.toLowerCase().includes(keyword)
            )
          );
          if (maleVoice) selectedVoice = maleVoice;
        }

        utterance.voice = selectedVoice;
      }

      utteranceRef.current = utterance;
    };

    // Handle async voice loading
    if (synth.getVoices().length > 0) {
      loadVoices();
    } else {
      synth.onvoiceschanged = loadVoices;
    }

    return () => {
      synth.cancel();
      synth.onvoiceschanged = null;
    };
  }, [language, rate, pitch, volume, preferMaleVoice]);

  const speak = useCallback((text) => {
    if (!utteranceRef.current || !text) return;

    window.speechSynthesis.cancel();
    utteranceRef.current.text = text;
    window.speechSynthesis.speak(utteranceRef.current);
  }, []);

  const stop = useCallback(() => {
    window.speechSynthesis.cancel();
  }, []);

  return { speak, stop };
};

export default useTextToSpeech;
